// ThemeContext.tsx
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { Theme } from '../types/theme';
import { loadThemeConfig, resolveTheme } from '../utils/themeLoader';
import { getDefaultTheme, getDefaultThemeSync } from '../utils/defaultTheme';
import ThemeMessageListener from '../components/ThemeCustomizer/ThemeMessageListener';
import { getRemoteEnv } from '../config/remoteEnv';

interface ThemeContextType {
    theme: Theme;
    updateTheme: (newTheme: Partial<Theme>) => void;
    isLoading: boolean;
}

interface ThemeProviderProps {
    children: ReactNode;
    mode?: 'light' | 'dark' | 'white'; // Backward compatibility prop
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

// Helper function to map mode to predefined theme names
const mapModeToTheme = (mode: 'light' | 'dark' | 'white'): string => {
    switch (mode) {
        case 'dark':
            return 'dark';
        case 'white':
            return 'white';
        case 'light':
        default:
            return getDefaultThemeSync();
    }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, mode }) => {
    const [theme, setTheme] = useState<Theme | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const env = getRemoteEnv();

    const applyTheme = useCallback((resolvedTheme: Theme) => {
        setTheme(resolvedTheme);

        const color = env.themeOverrideAccentColor || resolvedTheme?.topbarBackgroundColor;
        if (color) {
            const meta = document.querySelector("meta[name='theme-color']") || (() => {
                const m = document.createElement("meta");
                m.name = "theme-color";
                document.head.appendChild(m);
                return m;
            })();
            meta.setAttribute("content", color);
        }
    }, []);

    useEffect(() => {
        const initializeTheme = async () => {
            try {
                let resolvedTheme: Theme;

                // CONTEXTUAL THEMING: If mode prop is provided, use it directly
                // This allows specific screens (like admin) to override client themes
                if (mode) {
                    const themeName = mapModeToTheme(mode);
                    const config = { predefinedTheme: themeName as any };
                    resolvedTheme = resolveTheme(config);
                } else {
                    // CLIENT THEMING: Load from JSON config for client-specific themes
                    const config = await loadThemeConfig();
                    resolvedTheme = resolveTheme(config);
                }

                applyTheme(resolvedTheme);
                
                // Save to localStorage for persistence
                localStorage.setItem('theme', JSON.stringify(resolvedTheme));
            } catch (error) {
                console.error('Error initializing theme:', error);
                
                // Fallback logic
                if (mode) {
                    // If mode was provided but something failed, use the mode-based theme
                    const themeName = mapModeToTheme(mode);
                    const config = { predefinedTheme: themeName as any };
                    applyTheme(resolveTheme(config));
                } else {
                    // Fallback to saved theme or default
                    const savedTheme = localStorage.getItem('theme');
                    if (savedTheme) {
                        applyTheme(JSON.parse(savedTheme));
                    } else {
                        // Use default theme
                        const defaultTheme = await getDefaultTheme();
                        const config = { predefinedTheme: defaultTheme };
                        applyTheme(resolveTheme(config));
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };

        initializeTheme();
    }, [mode, applyTheme]); // Re-run if mode changes

    const updateTheme = (newTheme: Partial<Theme>) => {
        if (!theme) return;
        
        const updatedTheme = {
            ...theme,
            ...newTheme,
        };
        
        applyTheme(updatedTheme);
        localStorage.setItem('theme', JSON.stringify(updatedTheme));
    };

    if (!theme) {
        return null; // or a loading spinner
    }

    return (
        <ThemeContext.Provider value={{ theme, updateTheme, isLoading }}>
            <ThemeMessageListener />
            {children}
        </ThemeContext.Provider>
    );
};